import type { FunctionComponent } from "react"

import { clsx } from "clsx"

import { Home } from "@jobteaser/spark/components/Icons/Home"

import { NEW_SIGN_IN_FLOW, NEW_SIGN_IN_FLOW_VARIANTS } from "@/modules/careerCenter/constants/flags"
import { useCareerCenterFetch } from "@/modules/careerCenter/hooks/useCareerCenterFetch"
import { useCareerCenterModulesFetch } from "@/modules/careerCenter/hooks/useCareerCenterModulesFetch"
import { isWwwCareerCenter } from "@/modules/careerCenter/isWwwCareerCenter"
import { isLinkActive } from "@/modules/headerFooterFO/Header/helpers/isLinkActive"
import { LoginButton } from "@/modules/headerFooterFO/Header/LoginUser/LoginButton"
import { getToolboxItem } from "@/modules/headerFooterFO/utils/getToolboxItem"
import { useTranslation } from "@/modules/i18n/components/useTranslation"
import { useLocale } from "@/modules/locales/useLocale"
import { Link } from "@/modules/routing/components/Link"
import { useHref } from "@/modules/routing/hooks/useHref"
import { useFlag } from "@/modules/unleash/hooks/useFlag"
import { useCurrentUserFetch } from "@/modules/user/client/hooks/useCurrentUserFetch"

import styles from "./NavV2.module.css"

export const NavV2: FunctionComponent = () => {
  const { t } = useTranslation(["module_header_fo"])
  const href = useHref()
  const locale = useLocale()
  const { dataCurrentUser } = useCurrentUserFetch()
  const { dataCareerCenter } = useCareerCenterFetch()
  const isNewSignInFlowEnabled = useFlag(NEW_SIGN_IN_FLOW, NEW_SIGN_IN_FLOW_VARIANTS.ENABLED)
  const { dataCareerCenterModules } = useCareerCenterModulesFetch()

  if (!dataCurrentUser || !dataCareerCenter || !dataCareerCenterModules) return null

  const {
    hasAdvices,
    hasAppointments,
    hasCareerExplorer,
    hasCareerServiceSpotlight,
    hasEvents,
    hasFreya,
    hasHester,
    hasInsights,
    hasJobOffers,
    hasJobRoles,
    hasMainCareerCenterRessources,
    hasMarco,
    hasRessources,
    hasVasco,
    mainCareerCenterResourcesModuleLocalizedName,
    resourcesModuleLocalizedName,
  } = dataCareerCenterModules

  const { idOld, mainCareerCenterIdOld } = dataCareerCenter

  const isWwwCC = isWwwCareerCenter(dataCareerCenter)

  const toolboxItem = getToolboxItem({
    careerCenterIdOld: idOld,
    hasAdvices,
    hasAppointments,
    hasCareerExplorer,
    hasCareerServiceSpotlight,
    hasFreya,
    hasHester,
    hasInsights,
    hasJobRoles,
    hasMainCareerCenterRessources,
    hasMarco,
    hasRessources,
    hasVasco,
    locale,
    mainCareerCenterIdOld,
    mainCareerCenterResourcesModuleLocalizedName,
    resourcesModuleLocalizedName,
  })

  const toolboxItemDisplayName = toolboxItem?.name.startsWith("module_header_fo.")
    ? t(toolboxItem.name as `module_header_fo.${string}`)
    : toolboxItem?.name

  return (
    <ul className={styles["app-Nav"]}>
      {/* Link to Homepage */}
      {!!dataCurrentUser?.attributes.isStudent && (
        <li className={clsx(styles["app-Nav__item"], styles["app-Nav__item--dashboard"])}>
          <Link
            className={clsx(styles["app-Nav__link"], {
              [styles["app-Nav__link--active"]]: isLinkActive(href, `/${locale}/dashboard`),
            })}
            href={`/${locale}/dashboard`}
          >
            {t("module_header_fo.nav_dashboard")}
          </Link>
        </li>
      )}

      {/* Home */}
      {dataCurrentUser?.isLogged && (
        <li className={clsx(styles["app-Nav__item"], styles["app-Nav__item--home"])}>
          <a
            className={clsx(styles["app-Nav__link"], {
              [styles["app-Nav__link--active"]]: isLinkActive(href, `/${locale}/dashboard`),
            })}
            href={`/${locale}/dashboard`}
          >
            <Home size={20} />
          </a>
        </li>
      )}

      {/* Offers */}
      {hasJobOffers && (
        <li className={clsx(styles["app-Nav__item"], styles["app-Nav__item--offers"])}>
          <Link
            className={clsx(styles["app-Nav__link"], {
              [styles["app-Nav__link--active"]]: isLinkActive(href, `/${locale}/job-offers`),
            })}
            href={`/${locale}/job-offers`}
          >
            {t("module_header_fo.nav_offers")}
          </Link>
        </li>
      )}

      {/* Events  */}
      {hasEvents && (
        <li className={clsx(styles["app-Nav__item"], styles["app-Nav__item--events"])}>
          <a
            className={clsx(styles["app-Nav__link"], {
              [styles["app-Nav__link--active"]]: isLinkActive(href, `/${locale}/events`),
            })}
            href={`/${locale}/events`}
          >
            {t("module_header_fo.nav_events")}
          </a>
        </li>
      )}

      {/* Companies */}
      <li className={clsx(styles["app-Nav__item"], styles["app-Nav__item--companies"])}>
        <a
          className={clsx(styles["app-Nav__link"], {
            [styles["app-Nav__link--active"]]: isLinkActive(href, `/${locale}/companies`),
          })}
          href={`/${locale}/companies`}
        >
          {!!dataCurrentUser?.attributes.hasCommunityModule && !!dataCareerCenter?.intranet
            ? t("module_header_fo.nav_companies_short")
            : t("module_header_fo.nav_companies")}
        </a>
      </li>

      {/* Toolbox  */}
      {toolboxItem && dataCurrentUser?.isLogged && (
        <li className={clsx(styles["app-Nav__item"], styles["app-Nav__item--toolbox"])}>
          <a
            className={clsx(styles["app-Nav__link"], {
              [styles["app-Nav__link--active"]]: isLinkActive(href, toolboxItem.href),
            })}
            href={toolboxItem.href}
          >
            {toolboxItemDisplayName}
          </a>
        </li>
      )}

      {/* Login  */}
      {isNewSignInFlowEnabled && !dataCurrentUser?.isLogged && (
        <li className={clsx(styles["app-Nav__item"], styles["app-Nav__item--login"])}>
          <LoginButton
            careerCenterName={dataCareerCenter?.name || ""}
            isWwwCareerCenter={isWwwCC}
            label={t("module_header_fo.login_button.label")}
            uiPosition="nav"
          />
        </li>
      )}
    </ul>
  )
}
